import React from 'react';
import { SchematicShell, Line, Rect } from './SchematicPrimitives';
import {
  WOOD_STOVE_LEG_HEIGHT_RATIO,
  WOOD_STOVE_TOP_PLATE_RATIO,
  WOOD_STOVE_HEARTH_THICKNESS,
  WOOD_STOVE_HEARTH_SIDE_MARGIN,
  WOOD_STOVE_HEARTH_FRONT_MARGIN,
  WOOD_STOVE_LEG_THICKNESS_RATIO,
  WOOD_STOVE_BODY_RATIO,
  WOOD_STOVE_DOOR_WIDTH_RATIO,
  WOOD_STOVE_DOOR_HEIGHT_RATIO,
  WOOD_STOVE_FLUE_RADIUS_RATIO,
  WOOD_STOVE_FLUE_HEIGHT_RATIO,
  WOOD_STOVE_FLUE_BACK_OFFSET_RATIO,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for a freestanding `wood-stove`.
 *
 * RoomPlan has no wood-stove category — one arrives as a `stove`, and
 * post-processing rule 1.6 retypes it when it stands in a living / dining /
 * bedroom section. The elevation is the most recognisable view of one, so it
 * draws the whole stack the 3D renderer builds, bottom up:
 *
 *   hearth pad → short legs → cast-iron body with a glass door →
 *   full-width cook-top plate → flue rising from the back
 *
 * Proportions come from the SAME constants `woodStoveRenderer` uses, so the
 * two views cannot disagree. Two glyphs deliberately overflow the object
 * box, exactly as the meshes do: the hearth spreads wider than the stove
 * (4" per side, 8" at the door) and the FLUE rises `0.28 × height` above
 * it — `SchematicShell` renders with `overflow: visible`.
 */
export function WoodStoveSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const viewportW = isSide ? depth : width;
  const stroke = { strokeColor, strokeWidth };

  // Mirror the renderer's vertical stack. SVG y grows downward, so y=0 is the
  // TOP of the object and y=height is the floor.
  const hearthH = Math.min(WOOD_STOVE_HEARTH_THICKNESS, height * 0.1);
  const standH = height - hearthH;
  const legH = standH * WOOD_STOVE_LEG_HEIGHT_RATIO;
  const plateH = standH * WOOD_STOVE_TOP_PLATE_RATIO;
  const bodyH = Math.max(standH - legH - plateH, 0.05);

  const bodyW = (isSide ? depth : width) * WOOD_STOVE_BODY_RATIO;
  const bodyX = (viewportW - bodyW) / 2;

  const floorY = height;
  const hearthTopY = floorY - hearthH;
  const legTopY = hearthTopY - legH;
  const bodyTopY = legTopY - bodyH;
  const plateTopY = bodyTopY - plateH;

  // The pad spreads beyond the stove: 4" per side; on a side view the extra
  // 8" door margin lands at the FRONT (+z maps to the right edge).
  const hearthLeft = bodyX - WOOD_STOVE_HEARTH_SIDE_MARGIN;
  const hearthRight = isSide
    ? bodyX + bodyW + WOOD_STOVE_HEARTH_FRONT_MARGIN
    : bodyX + bodyW + WOOD_STOVE_HEARTH_SIDE_MARGIN;

  // Legs exactly where the renderer puts them: square section
  // `min(w,d) × ratio`, centered `bodyHalf − legSize` off the middle.
  const legW = Math.min(width, depth) * WOOD_STOVE_LEG_THICKNESS_RATIO;
  const legOffset = bodyW / 2 - legW;
  const legCenters = [viewportW / 2 - legOffset, viewportW / 2 + legOffset];

  // The cook-top plate spans the FULL object box (it overhangs the body),
  // which is what gives a stove its stepped silhouette.
  const plateW = viewportW;

  // Glass door, centered on the body's front.
  const doorW = bodyW * WOOD_STOVE_DOOR_WIDTH_RATIO;
  const doorH = bodyH * WOOD_STOVE_DOOR_HEIGHT_RATIO;
  const doorX = viewportW / 2 - doorW / 2;
  const doorY = bodyTopY + (bodyH - doorH) / 2;
  // Paper-only legibility inset: the 3D glass sits 4% inside the door
  // frame, which collapses into a double-stroke at elevation scale.
  const glassInset = Math.min(doorW, doorH) * 0.12;

  // Flue: a cylinder rising above the box, at the middle of a front view
  // and `0.25 × depth` behind center on a side view (-z maps left).
  const flueW = Math.min(width, depth) * WOOD_STOVE_FLUE_RADIUS_RATIO * 2;
  const flueCx = isSide
    ? depth / 2 - depth * WOOD_STOVE_FLUE_BACK_OFFSET_RATIO
    : viewportW / 2;
  const flueTopY = -height * WOOD_STOVE_FLUE_HEIGHT_RATIO;

  return (
    <SchematicShell
      width={viewportW}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Hearth pad — stands proud of the stove on every side. */}
      <Rect
        x={hearthLeft}
        y={hearthTopY}
        width={hearthRight - hearthLeft}
        height={hearthH}
        {...stroke}
      />

      {/* Two legs, so the body reads as standing clear of the pad. */}
      {legCenters.map((lc, i) => (
        <Rect
          key={`leg-${i}`}
          x={lc - legW / 2}
          y={legTopY}
          width={legW}
          height={legH}
          {...stroke}
        />
      ))}

      {/* Cast-iron body. */}
      <Rect x={bodyX} y={bodyTopY} width={bodyW} height={bodyH} {...stroke} />

      {/* Glass door — only on the front; from the side you see the body
          flank. Double outline: door frame + glass. */}
      {!isSide && (
        <g data-part="door">
          <Rect x={doorX} y={doorY} width={doorW} height={doorH} {...stroke} />
          <Rect
            x={doorX + glassInset}
            y={doorY + glassInset}
            width={doorW - glassInset * 2}
            height={doorH - glassInset * 2}
            {...stroke}
          />
        </g>
      )}

      {/* Cook-top plate — full box width, overhanging the body. */}
      <Rect x={0} y={plateTopY} width={plateW} height={plateH} {...stroke} />

      {/* Flue pipe rising above the box from the plate, with a top cap. */}
      <g data-part="flue">
        <Line
          x1={flueCx - flueW / 2}
          y1={plateTopY}
          x2={flueCx - flueW / 2}
          y2={flueTopY}
          {...stroke}
        />
        <Line
          x1={flueCx + flueW / 2}
          y1={plateTopY}
          x2={flueCx + flueW / 2}
          y2={flueTopY}
          {...stroke}
        />
        <Line
          x1={flueCx - flueW / 2}
          y1={flueTopY}
          x2={flueCx + flueW / 2}
          y2={flueTopY}
          {...stroke}
        />
      </g>
    </SchematicShell>
  );
}

WoodStoveSchematic.supportsSideViews = true;
